import { ButtonKind, ButtonType, SpsIcon } from "@spscommerce/ds-shared";
import clsx from "clsx";
import * as React from "react";
import invariant from "tiny-invariant";

import * as PropTypes from "../prop-types";
import { PropsType } from "../PropsType";
import { spsGlobalPropTypes } from "../util";

export const propTypes = {
    ...spsGlobalPropTypes,
    disabled: PropTypes.bool,
    href: PropTypes.string,
    icon: PropTypes.enumValue<SpsIcon>(SpsIcon),
    kind: PropTypes.enumValue<ButtonKind>(ButtonKind),
    preset: PropTypes.deprecated(PropTypes.enumValue<ButtonKind>(ButtonKind)),
    spinning: PropTypes.bool,
    spinningTitle: PropTypes.string,
    type: PropTypes.enumValue<ButtonType>(ButtonType),
};

export type SpsButtonProps = PropsType<typeof propTypes> & React.HTMLAttributes<HTMLButtonElement | HTMLAnchorElement>;

export function SpsButton(props: SpsButtonProps) {
    const {
        children,
        className,
        disabled,
        href,
        icon,
        kind,
        preset,
        spinning,
        spinningTitle = "Loading...",
        type = ButtonType.BUTTON,
        unsafelyReplaceClassName,
        ...rest
    } = props;

    const kindOrPreset = kind || preset || ButtonKind.DEFAULT;

    React.useEffect(() => {
        invariant(typeof preset === "undefined", `The 'preset' prop on SpsButton has been renamed to 'kind'`);
    }, [preset]);

    const classes = clsx(
        unsafelyReplaceClassName || "sps-btn",
        `sps-btn--${kindOrPreset}`,
        spinning && "sps-btn--spinning",
        disabled && "disabled",
        className,
    );

    const contents = [
        icon && <i className={clsx("sps-icon", `sps-icon-${icon}`)} aria-hidden="true" key={0}></i>,
        children,
        <div className="sps-spinner" key={1}>{spinningTitle}</div>
    ];

    return (
        <div className={classes}>{
            href
            ? <a href={href} {...rest}>{...contents}</a>
            : <button type={type} disabled={disabled} {...rest}>{...contents}</button>
        }</div>
    );
}

Object.assign(SpsButton, {
    propTypes,
    displayName: "SpsButton"
});
